import React from 'react';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';

const VALUES = [
  { value: 'Honesty', icon: '🤝', color: 'from-blue-500 to-blue-600' },
  { value: 'Hard Work', icon: '💪', color: 'from-primary to-primary-dark' },
  { value: 'Respect for Others', icon: '🙏', color: 'from-purple-500 to-purple-600' },
  { value: 'Cleanliness', icon: '✨', color: 'from-cyan-500 to-cyan-600' },
  { value: 'Kindness & Love', icon: '❤️', color: 'from-accent to-accent-dark' },
  { value: 'Justice', icon: '⚖️', color: 'from-indigo-500 to-indigo-600' },
  { value: 'Forgiveness', icon: '🕊️', color: 'from-teal-500 to-teal-600' },
  { value: 'Thankfulness', icon: '🙏', color: 'from-orange-500 to-orange-600' },
  { value: 'Compassion', icon: '💝', color: 'from-pink-500 to-pink-600' },
  { value: 'Tolerance', icon: '🌍', color: 'from-green-500 to-green-600' },
  { value: 'Cooperation', icon: '🤝', color: 'from-emerald-500 to-emerald-600' },
];

const PRINCIPALS = [
  {
    name: 'Syed Muhammad Ilyas',
    role: 'Regional Head',
    initial: 'S',
    color: 'from-primary to-primary-dark',
    message: 'Welcome to PAK School & College System Mardan! Our community is dedicated to academic excellence, personal growth, and the pursuit of knowledge. We prioritize student-centered learning, fostering an environment that encourages 21st century skills like curiosity, critical thinking, and collaboration.',
  },
  {
    name: 'Muhammad Jehanzeb',
    role: 'Vice Principal, Par Hoti',
    initial: 'M',
    color: 'from-purple-500 to-purple-700',
    urdu: '"سبق پھر پڑھ صداقت کا، عدالت کا، شجاعت کا — لیا جائے گا تجھ سے کام دنیا کی امامت کا" — علامہ اقبال',
    message: 'At PAK School & College System, we are inspired by Allama Iqbal\'s vision—nurturing students with truth, justice, and courage to lead with knowledge, faith, and integrity.',
  },
  {
    name: 'Sadia Qamar',
    role: 'Principal, DHA Campus',
    initial: 'S',
    color: 'from-pink-500 to-pink-700',
    message: 'Welcome to PAK School DHA Campus, where we are committed to nurturing young minds with excellence in education. Established 27 years ago, our school provides a holistic learning environment for students from Play Group to Grade 5.',
  },
  {
    name: 'Mrs Minhaj',
    role: 'Principal, SMT Campus',
    initial: 'M',
    color: 'from-amber-500 to-amber-700',
    message: 'I am honoured to lead this vibrant community of learners. Our dedicated teachers and staff are committed to provide a supportive and inclusive environment that fosters academic excellence and personal growth with a focus on character building.',
  },
];

const DIFFERENTIATORS = [
  { icon: 'fa-lightbulb', title: 'Innovation in Education', desc: 'At the heart of our ethos lies a commitment to innovation in education, integrating technology with traditional values.', color: 'bg-primary/10 text-primary' },
  { icon: 'fa-user-graduate', title: 'Student-Centric Approach', desc: 'Our dedicated and experienced faculty embodies a student-centric approach, tailoring learning to individual needs.', color: 'bg-accent/10 text-accent' },
  { icon: 'fa-globe', title: 'Global Perspective', desc: 'We impart a global perspective, celebrating cultural diversity and inclusion within our school community.', color: 'bg-green-100 text-green-600' },
  { icon: 'fa-star', title: 'Character Development', desc: 'Character development is woven into the fabric of our educational philosophy and daily school life.', color: 'bg-purple-100 text-purple-600' },
  { icon: 'fa-hands-helping', title: 'Community Engagement', desc: 'Beyond academics, we foster a spirit of community engagement and service, preparing responsible citizens.', color: 'bg-amber-100 text-amber-600' },
];

const About = () => (
  <div className="page-enter">
    <Helmet>
      <title>About Us — PAK School &amp; College System | Mission, Vision &amp; Values</title>
      <meta name="description" content="Learn about PAK School & College System's mission to provide quality education with character building. Discover our vision 'Every Student A Star' and core values." />
      <meta name="keywords" content="About PAK School, School Mission, School Vision, Educational Values, Character Building, Quality Education, Mardan School" />
      <link rel="canonical" href="https://pakschoolandcollege.com/about" />
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://pakschoolandcollege.com/" },
          { "@type": "ListItem", "position": 2, "name": "About Us", "item": "https://pakschoolandcollege.com/about" }
        ]
      })}</script>
    </Helmet>

    {/* Page Hero */}
    <div className="page-hero">
      <div className="page-hero-content">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white/80 text-sm font-inter mb-5">
          🌟 Every Student A Star
        </div>
        <h1>About <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">PAK School</span></h1>
        <p>Nurturing excellence, fostering character, and empowering every student to shine like a star</p>
      </div>
    </div>

    {/* Our Values */}
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="section-badge mx-auto w-fit">Our Foundation</div>
          <h2 className="section-title">Our Core Values</h2>
          <p className="section-subtitle mx-auto">The principles that guide our educational philosophy and shape our students' character</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {VALUES.map((item, i) => (
            <div key={i} className="group bg-white rounded-2xl border border-gray-100 p-5 text-center shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center text-2xl mx-auto mb-3 group-hover:scale-110 transition-transform duration-300`}>
                {item.icon}
              </div>
              <p className="font-poppins font-600 text-navy text-sm leading-tight">{item.value}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Mission */}
    <section className="py-20 bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Mission */}
          <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-bl-full" />
            <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
              <i className="fas fa-bullseye text-primary text-xl" />
            </div>
            <div className="inline-block bg-primary/10 text-primary text-xs font-poppins font-700 px-3 py-1 rounded-full mb-3 uppercase tracking-wider">Our Mission</div>
            <p className="text-gray-600 font-inter leading-relaxed text-base">
              To give quality education with major emphasis on character building, applied knowledge and skills, enabling the students to successfully enter the professional colleges, with sound educational background.
            </p>
          </div>

          {/* Vision */}
          <div className="bg-gradient-to-br from-navy to-blue-900 rounded-3xl p-8 shadow-lg relative overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-white/5 -translate-y-10 translate-x-10" />
            <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center mb-5">
              <i className="fas fa-star text-yellow-400 text-xl" />
            </div>
            <div className="inline-block bg-white/15 text-white/80 text-xs font-poppins font-700 px-3 py-1 rounded-full mb-3 uppercase tracking-wider">Our Vision</div>
            <h3 className="font-poppins font-800 text-white text-2xl mb-3">Every Student A Star</h3>
            <p className="text-white/75 font-inter leading-relaxed text-base">
              At our school, our vision is to empower every student to shine brilliantly like a star, fostering their unique talents and potential in an environment of excellence and innovation.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* CEO Message */}
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="section-badge mx-auto w-fit">Leadership</div>
          <h2 className="section-title">Message from the CEO</h2>
        </div>
        <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-3xl p-8 sm:p-10 border border-gray-100 relative overflow-hidden">
          <div className="text-primary/15 font-poppins leading-none absolute top-6 left-8" style={{ fontSize: '100px' }}>"</div>
          <div className="flex flex-col sm:flex-row gap-6 items-start mb-6 relative">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center font-poppins font-800 text-white text-2xl flex-shrink-0">W</div>
            <div>
              <h3 className="font-poppins font-700 text-navy text-xl">Mrs. Wasim</h3>
              <p className="text-primary font-inter text-sm mt-0.5">CEO, PAK School &amp; College System Mardan</p>
            </div>
          </div>
          <div className="space-y-4 text-gray-600 font-inter leading-relaxed relative">
            <p>As CEO of our esteemed school system, I'm thrilled to welcome you to our vibrant community dedicated to nurturing the brilliance within each and every one of our students.</p>
            <p>At PAK School &amp; College System, our mission is simple yet profound: <strong className="text-navy">"Every Student A Star"</strong> — to ignite a passion for learning and empower every student to shine brightly in their own unique way.</p>
            <p>Together, let's embark on a transformative journey where every student's journey is guided by their own constellation of dreams and aspirations. Welcome to The PAK School &amp; College System Mardan, where each student's potential truly becomes a shining star.</p>
          </div>
        </div>
      </div>
    </section>

    {/* Principals Messages */}
    <section className="py-20 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="section-badge mx-auto w-fit">Our Leaders</div>
          <h2 className="section-title">Messages from Heads &amp; Principals</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {PRINCIPALS.map((person, i) => (
            <div key={i} className="bg-white rounded-2xl p-7 shadow-sm border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <div className="flex items-center gap-4 mb-5">
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${person.color} flex items-center justify-center font-poppins font-800 text-white text-xl flex-shrink-0`}>
                  {person.initial}
                </div>
                <div>
                  <h3 className="font-poppins font-700 text-navy text-base">{person.name}</h3>
                  <p className="text-gray-400 text-sm font-inter">{person.role}</p>
                </div>
              </div>
              {person.urdu && (
                <p className="text-gray-500 text-sm font-inter italic text-right mb-4 border-r-2 border-primary/30 pr-3 leading-relaxed" dir="rtl">
                  {person.urdu}
                </p>
              )}
              <p className="text-gray-600 font-inter text-sm leading-relaxed">{person.message}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* What Makes Us Different */}
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="section-badge mx-auto w-fit">Our Edge</div>
          <h2 className="section-title">What Makes Us Different?</h2>
          <p className="section-subtitle mx-auto">
            A distinctive blend of innovation, inclusivity, and unwavering commitment to excellence sets us apart.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {DIFFERENTIATORS.map((item, i) => (
            <div key={i} className="group bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <div className={`w-12 h-12 rounded-xl ${item.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                <i className={`fas ${item.icon} text-lg`} />
              </div>
              <h3 className="font-poppins font-700 text-navy text-base mb-2">{item.title}</h3>
              <p className="text-gray-500 font-inter text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <Footer />
  </div>
);

export default About;
